/*
 * Central data-source for the app.
 *
 * All well/fleet data comes from the API (/api/...), which serves JSON computed
 * from a local store kept live from Databricks. To avoid re-downloading data we
 * already hold, every response's ETag is remembered; subsequent requests send
 * If-None-Match and, on a 304, we return the cached body instead of downloading
 * again. A force-refresh bypasses the cache (and can trigger a server pull).
 *
 * Falls back to the static PUBLIC_URL JSON files when the API is unavailable
 * (e.g. a plain `npm run build` opened without the server), so the app still
 * works offline against the last-built snapshot.
 */

const API = '/api';
const STATIC = process.env.PUBLIC_URL || '';

// Token getter — set by the AuthProvider at app boot.
let _getAccessToken = async () => null;
export function setTokenGetter(fn) { _getAccessToken = fn; }

// Called when the server rejects a request as unauthenticated (401) — wired
// to AuthProvider's forceReLogin so an expired session triggers a clean
// sign-in redirect instead of silently falling back to stale cached data.
// Never for 403 (valid session, missing role) — that would redirect-loop.
let _onAuthExpired = () => {};
export function setAuthExpiredHandler(fn) { _onAuthExpired = fn; }

// In-memory body cache (per session) + persistent ETag map (across reloads).
const bodyCache = new Map();
const ETAG_KEY = 'wht_etags_v1';

function loadEtags() {
  try { return JSON.parse(localStorage.getItem(ETAG_KEY) || '{}'); } catch { return {}; }
}
function saveEtag(url, etag) {
  try {
    const m = loadEtags();
    m[url] = etag;
    localStorage.setItem(ETAG_KEY, JSON.stringify(m));
  } catch { /* ignore quota */ }
}

const parseJsonSafe = (text) => JSON.parse(text.replace(/\bNaN\b/g, 'null'));

/**
 * Fetch JSON from the API with ETag-aware caching.
 *  - force: bypass the local body cache and send no If-None-Match.
 * Returns parsed JSON.
 */
async function apiJson(pathname, { force = false } = {}) {
  const url = `${API}${pathname}`;
  const etags = loadEtags();
  const headers = {};
  if (!force && etags[url] && bodyCache.has(url)) {
    headers['If-None-Match'] = etags[url];
  }
  if (force) headers['Cache-Control'] = 'no-cache';

  const token = await _getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(url, { headers });
  if (res.status === 304 && bodyCache.has(url)) {
    return bodyCache.get(url); // unchanged — reuse, no re-download of the body
  }
  // Only a token the server rejected means the session is actually dead.
  // No token at all is usually just a boot-time race (MSAL still restoring
  // the account) — reacting to that here caused a redirect loop.
  if (res.status === 401 && token) _onAuthExpired();
  if (!res.ok) {
    const err = new Error(`API ${pathname}: ${res.status}`);
    err.status = res.status;
    throw err;
  }

  const etag = res.headers.get('ETag');
  const text = await res.text();
  const data = parseJsonSafe(text);
  if (etag) { saveEtag(url, etag); bodyCache.set(url, data); }
  return data;
}

// Static fallback (no server): read the built JSON files directly.
async function staticJson(file) {
  const res = await fetch(`${STATIC}${file}`);
  if (!res.ok) throw new Error(`static ${file}: ${res.status}`);
  return parseJsonSafe(await res.text());
}

async function withFallback(apiCall, staticFile) {
  try { return await apiCall(); }
  catch (e) {
    // A 401 means the session expired — don't mask that with stale bundled
    // data (the user would just see an app that looks fine but isn't).
    if (e.status === 401) throw e;
    if (staticFile) return staticJson(staticFile);
    throw e;
  }
}

// --- public data accessors ------------------------------------------------

export const getWellIndex = (opts) =>
  withFallback(() => apiJson('/wells', opts), '/well_index.json');

export const getWell = (id, opts) =>
  withFallback(() => apiJson(`/wells/${id}`, opts), `/wells/${id}.json`);

export const getWellRecent = (id, opts) =>
  withFallback(() => apiJson(`/wells/${id}/recent`, opts), `/wells/${id}_recent.json`);

export const getFleet = (opts) =>
  withFallback(() => apiJson('/fleet', opts), '/fleet_fill_rates.json');

export const getStatus = async () => {
  try {
    const headers = {};
    const token = await _getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const r = await fetch(`${API}/status`, { headers });
    if (r.status === 401 && token) _onAuthExpired();
    return r.ok ? r.json() : null;
  } catch { return null; }
};

// Force the server to check Databricks for new data and recompute if needed.
// force=true recomputes even when there are no new rows.
export async function triggerRefresh(force = false) {
  const headers = { 'Content-Type': 'application/json' };
  const token = await _getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const r = await fetch(`${API}/refresh${force ? '?force=1' : ''}`, { method: 'POST', headers });
  if (r.status === 401 && token) _onAuthExpired();
  const body = await r.json().catch(() => ({}));
  if (!r.ok) {
    const err = new Error(body.error || `refresh: ${r.status}`);
    err.authRequired = r.status === 428 || !!body.authRequired;
    err.detail = body.error || err.message;
    throw err;
  }
  return body;
}

// Drop cached bodies so the next load re-fetches (used after a server refresh).
export function invalidateCache() {
  bodyCache.clear();
}
